// The deterministic instance builder — everything that turns tiles into
// placed scatter instances. Split out of TerrainScatter.tsx (which keeps the
// React instancing components); the configs and palettes live in
// scatterConfigs.ts / scatterPalettes.ts and are re-exported from here.

import type { SceneNode } from '@aod/protocol';
import * as THREE from 'three';
import type { ScatterConfig, ScatterPropType } from './TerrainScatterMaterials.js';
import {
  buildBiomeMap,
  hexNoise,
  landBaseHeight,
  smoothField,
  surfaceYAt,
  terrainLevelsActive,
  tileSurfaceY,
} from './elevation.js';
import { TREES } from './scatterConfigs.js';
import { FOLIAGE_PALETTES, TRUNK_PALETTES, jitterColor, samplePalette } from './scatterPalettes.js';
import {
  FROST_COLORS,
  applySeasonTint,
  foliageClassOf,
  foliageSeasonTint,
  grassSeasonTint,
  seasonPhase,
} from './seasons.js';
import { EDGE_NEIGHBOURS } from './terrainEdges.js';
import { deriveVolcanoes, volcanoScatterClearTiles } from './volcano.js';

export {
  SCATTER_CONFIGS,
  SHADOW_RADIUS,
  TREES,
  configAppliesToClimate,
} from './scatterConfigs.js';
export {
  FOLIAGE_PALETTES,
  TRUNK_PALETTES,
  jitterColor,
  samplePalette,
} from './scatterPalettes.js';

const HEX_HALF = 0.85;

// Module-level temps — mutated in the fill loops. Safe because JS is single-threaded.
const _color = new THREE.Color();

/** Hash a hex coordinate + instance index into [-1,1) offsets. */
function scatterOffset(q: number, r: number, idx: number, salt: number): [number, number] {
  const h = hexNoise(q + idx * 0.37 + salt, r - idx * 0.73);
  return [h * 2 - 1, hexNoise(r + idx * 0.51, q - idx * 0.29 + salt) * 2 - 1];
}

/** Check if world position [wx, wz] falls within a hex centered at [cx, cz]. */
/**
 * Whether (wx, wz) lies inside the pointy-top hex of circumradius `r`
 * centred on (cx, cz). (This used cube distance <= 1, which reaches all the
 * way to the neighbouring tiles' centres, so props were planted on the next
 * tile over and got buried in its hills and mountains.)
 */
export function pointInHex(wx: number, wz: number, cx: number, cz: number, r: number): boolean {
  const ax = Math.abs(wx - cx);
  const az = Math.abs(wz - cz);
  return ax <= (Math.sqrt(3) / 2) * r && az <= r - ax / Math.sqrt(3);
}

export interface ScatterInstance {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly scaleX: number;
  readonly scaleY: number;
  readonly scaleZ: number;
  readonly rotY: number;
  readonly tiltX: number;
  readonly tiltZ: number;
  /** Linear-space RGB for the 'main' sub-meshes (foliage, rock body...). */
  readonly mainColor: readonly [number, number, number] | null;
  /** Linear-space RGB for 'trunk' sub-meshes; null when the prop has none. */
  readonly trunkColor: readonly [number, number, number] | null;
}

/** Pick a weighted prop for a hash in [0,1). */
export function pickProp(props: ScatterConfig['props'], h: number): ScatterPropType {
  const total = props.reduce((n, p) => n + p.weight, 0);
  let acc = 0;
  for (const p of props) {
    acc += p.weight / total;
    if (h < acc) return p.propType;
  }
  return props[props.length - 1]!.propType;
}

export function saltOf(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 997;
  return h * 0.173;
}

/**
 * Deterministic scatter for one config, grouped by the prop type each
 * instance picked. Pure: same tiles in (and same season day), same instances
 * out. `seasonDay` (1-based day of year) tints foliage and grass; 0/undefined
 * skips season work entirely.
 */
export function buildScatterInstances(
  tiles: ReadonlyArray<SceneNode>,
  config: ScatterConfig,
  clearCoords?: ReadonlySet<string>,
  seasonDay = 0,
  detail = 1,
): Map<ScatterPropType, ScatterInstance[]> {
  const out = new Map<ScatterPropType, ScatterInstance[]>();
  const salt = saltOf(config.id);
  const clearsHere = config.props.some((p) => TREES.has(p.propType));
  const biomeMap = buildBiomeMap(tiles);
  // Tile centres by axial coord — the taller-neighbour check below needs the
  // neighbour's rendered position, and tiles arrive as scene nodes.
  const tileCentres = new Map<string, [number, number]>();
  for (const tile of tiles) {
    const p = tile.props as Record<string, unknown> | undefined;
    tileCentres.set(`${p?.q ?? 0},${p?.r ?? 0}`, [
      tile.transform.position[0]!,
      tile.transform.position[2]!,
    ]);
  }
  // Volcano tiles carry no scatter (a rock in front of the crater reads as a
  // bug). Gated on levels like the Volcanoes renderer, so default boards are
  // untouched; one derivation per build, no per-frame work.
  const volcanoTiles = terrainLevelsActive()
    ? volcanoScatterClearTiles(deriveVolcanoes(tiles))
    : undefined;

  for (const tile of tiles) {
    const props = tile.props as Record<string, unknown> | undefined;
    const q = (props?.q as number) ?? 0;
    const r = (props?.r as number) ?? 0;
    const biome = (props?.biome as string) ?? 'plains';
    if (volcanoTiles?.has(`${q},${r}`)) continue;
    if (!config.biomes.includes(biome)) continue;
    if (clearsHere && clearCoords?.has(`${q},${r}`)) continue;

    const tileH = landBaseHeight(q, r, biome);
    // A taller neighbour's prism buries the rim of this tile: the wall (and
    // on mountains the overhanging skirt) covers the ground right across the
    // shared edge, so a prop planted there vanishes inside it — the audit's
    // "props not buried" found bushes a full radius into the neighbouring
    // mountain's hex. Props that would stand inside a taller neighbour's
    // inflated hex are simply not placed; the density loss at mountain feet
    // is the point. `tileSurfaceY` is the same plateau the meshes are built
    // from, so the check can never disagree with the geometry.
    const tallerNeighbours: Array<[number, number]> = [];
    const tileTop = tileSurfaceY(q, r, biomeMap);
    for (const [dq, dr] of EDGE_NEIGHBOURS) {
      const nb = tileCentres.get(`${q + dq},${r + dr}`);
      if (!nb) continue;
      if (tileSurfaceY(q + dq, r + dr, biomeMap) > tileTop + 0.05) tallerNeighbours.push(nb);
    }
    // Snow boulders only above the snow line.
    if (config.props[0]?.propType === 'snowBoulder' && tileH < 0.42) continue;
    // Winter-hidden props (flowers) drop out entirely once winter sets in.
    if (config.hideInWinter && seasonDay > 0 && seasonPhase(seasonDay).season === 'winter') {
      continue;
    }

    const countSeed = hexNoise(q * 2.11 + salt, r * 3.17);
    const detMul = detail > 1 ? 1.6 : 1;
    // Whole-neighbourhood density: a low-frequency field makes some tiles
    // lush and others sparse, so the map reads as meadows and barrens
    // instead of a uniform carpet.
    const densityMul = 0.5 + smoothField(q * 0.11 + salt, r * 0.11) * 0.9;
    const count = Math.round(
      (config.minCount + Math.floor(countSeed * (config.maxCount - config.minCount + 1))) *
        detMul *
        densityMul,
    );
    const [tx, , tz] = tile.transform.position;
    const sizeMul = config.scale ?? 1;

    // Patchy placement: pick 1–2 cluster centres per tile, then drop
    // instances around them, so grass grows in tufts-meadows rather than
    // as uniformly dispersed single blades.
    const patchCount = config.patchy ? 1 + Math.floor(hexNoise(q - salt, r + salt) * 2.999) : 0;
    const patchCentres: Array<[number, number]> = Array.from({ length: patchCount }, (_, p) => {
      const [ox, oz] = scatterOffset(q, r, p * 7 + 3, salt + 11);
      return [ox * 0.42 * HEX_HALF, oz * 0.42 * HEX_HALF];
    });

    for (let i = 0; i < count; i++) {
      let ox: number;
      let oz: number;
      if (config.patchy && patchCentres.length > 0) {
        const [px, pz] = patchCentres[i % patchCentres.length]!;
        const [jx, jz] = scatterOffset(q, r, i, salt + 23);
        ox = px + jx * 0.24 * HEX_HALF;
        oz = pz + jz * 0.24 * HEX_HALF;
      } else {
        [ox, oz] = scatterOffset(q, r, i, salt);
        ox *= HEX_HALF;
        oz *= HEX_HALF;
      }
      const wx = tx + ox;
      const wz = tz + oz;
      if (!pointInHex(wx, wz, tx, tz, HEX_HALF)) continue;
      if (
        tallerNeighbours.length > 0 &&
        tallerNeighbours.some(([nx, nz]) => pointInHex(wx, wz, nx, nz, HEX_HALF * 1.5))
      ) {
        continue;
      }

      const h1 = hexNoise(wx * 3.1 + salt, wz * 2.7);
      const h2 = hexNoise(wz * 1.9, wx * 4.3 + salt);
      const h3 = hexNoise(wx * 5.3 - salt, wz * 0.7 + i);
      const propType = pickProp(config.props, h1);
      const isTree = TREES.has(propType);

      // Shape: trees vary height and width independently (tall-thin vs
      // short-broad) and lean slightly; other props scale uniformly.
      const size = (isTree ? 0.72 + h2 * 0.6 : 0.8 + h2 * 0.4) * sizeMul;
      const heightF = isTree ? 0.85 + h3 * 0.35 : 1;
      const widthF = isTree ? 0.9 + hexNoise(wx + 7.7, wz - 3.3) * 0.2 : 1;
      const leanX = isTree ? (hexNoise(wx - 1.3, wz + 9.1) * 2 - 1) * 0.07 : 0;
      const leanZ = isTree ? (hexNoise(wz + 4.4, wx - 2.2) * 2 - 1) * 0.07 : 0;

      // Colour: shared low-frequency field (groves) + per-instance jitter,
      // then the season tint (day-granular, so groves turn together).
      const field = smoothField(wx * 0.22 + salt, wz * 0.22);
      const palette = FOLIAGE_PALETTES[propType];
      let mainColor: [number, number, number] | null = null;
      if (palette) {
        let c: THREE.Color;
        if (config.perInstanceColor) {
          // Discrete stops — RGB-lerping between distant hues (red → blue)
          // passes through grey; wildflowers read as solid distinct colours.
          c = jitterColor(
            new THREE.Color(palette[Math.floor(h3 * palette.length) % palette.length]!),
            h2,
            h3,
          );
        } else {
          c = jitterColor(samplePalette(palette, field * 0.8 + h3 * 0.2, _color), h2, h3);
        }
        if (seasonDay > 0) {
          if (propType === 'grass') {
            applySeasonTint(c, grassSeasonTint(seasonDay), FROST_COLORS.grass);
          } else {
            const tint = foliageSeasonTint(seasonDay, propType);
            if (tint) {
              applySeasonTint(
                c,
                tint,
                foliageClassOf(propType) === 'deciduous'
                  ? FROST_COLORS.deciduous
                  : FROST_COLORS.conifer,
              );
            }
          }
        }
        mainColor = c.toArray() as [number, number, number];
      }
      const trunkPalette = TRUNK_PALETTES[propType];
      const trunkColor = trunkPalette
        ? (samplePalette(trunkPalette, h2, _color).toArray() as [number, number, number])
        : null;

      const inst: ScatterInstance = {
        x: wx,
        // Sink bases slightly so props don't float over the top-face noise.
        y: surfaceYAt(q, r, biomeMap, wx - tx, wz - tz) - 0.02 + config.yOffset,
        z: wz,
        scaleX: size * widthF,
        scaleY: size * heightF,
        scaleZ: size * widthF,
        rotY: hexNoise(q - i * 0.3, r + i * 0.7) * Math.PI * 2,
        tiltX: propType === 'rock' ? hexNoise(wx, wz) * Math.PI : leanX,
        tiltZ: leanZ,
        mainColor,
        trunkColor,
      };
      const list = out.get(propType);
      if (list) list.push(inst);
      else out.set(propType, [inst]);
    }
  }
  return out;
}
